
import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useReagentRequestsQuery } from "@/hooks/useReagentRequestsQuery";
import { useReagentRequestFilters } from "@/hooks/useReagentRequestFilters";
import ReagentRequestPageHeader from "@/components/reagentRequests/ReagentRequestPageHeader";
import ReagentRequestFilters from "@/components/reagentRequests/ReagentRequestFilters";
import ReagentRequestList from "@/components/reagentRequests/ReagentRequestList";
import ErrorBanner from "@/components/reagents/ErrorBanner";
import EditReagentRequestDialog from "@/components/reagentRequests/EditReagentRequestDialog";
import { toast } from "sonner";
import { ReagentRequest } from "@/types/reagentRequest.types";
import { updateReagentRequest } from "@/services/reagentRequests";
import { useAuth } from "@/contexts/auth";

const RequestedReagentsPage: React.FC = () => {
  const { user } = useAuth();
  const [editingRequest, setEditingRequest] = useState<ReagentRequest | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch and manage reagent request data
  const { 
    requests, 
    categories, 
    locations,
    isLoading, 
    error, 
    isError,
    refetch, 
    isRetrying 
  } = useReagentRequestsQuery();

  // Manage filters and filtered data
  const {
    searchTerm,
    setSearchTerm,
    selectedCategory,
    setSelectedCategory,
    selectedLocation,
    setSelectedLocation,
    showFilters,
    setShowFilters,
    filteredRequests,
    resetFilters
  } = useReagentRequestFilters({ requests });

  const handleEditRequest = (request: ReagentRequest) => {
    setEditingRequest(request);
  };

  const handleCloseEditDialog = () => {
    setEditingRequest(null);
  };

  const handleSaveRequest = async (updatedData: Partial<ReagentRequest>) => {
    if (!editingRequest || !user) return;

    // Ensure the user is the owner of the request
    if (editingRequest.user_id !== user.id) {
      toast.error("You don't have permission to edit this request");
      return;
    }

    setIsSubmitting(true);
    
    try {
      const success = await updateReagentRequest(editingRequest.id, updatedData);
      
      if (success) {
        toast.success("Reagent request updated successfully");
        refetch();
        setEditingRequest(null);
      } else {
        toast.error("Failed to update reagent request");
      }
    } catch (error) {
      console.error("Error updating reagent request:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow">
        <ReagentRequestPageHeader 
          showFilters={showFilters}
          toggleFilters={() => setShowFilters(!showFilters)}
          handleRefresh={refetch}
          isRetrying={isRetrying}
        />
        
        <ErrorBanner error={error} visible={isError} />
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <ReagentRequestFilters 
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedLocation={selectedLocation}
            setSelectedLocation={setSelectedLocation}
            categories={categories}
            locations={locations.filter(loc => !!loc)} // Filter out any null or empty location values
            resetFilters={resetFilters}
            showFilters={showFilters}
            toggleFilters={() => setShowFilters(!showFilters)}
          />
          
          <ReagentRequestList 
            requests={filteredRequests}
            isLoading={isLoading}
            error={error}
            refetch={refetch}
            resetFilters={resetFilters}
            onEditRequest={handleEditRequest}
          />
        </div>
      </div>
      
      <EditReagentRequestDialog
        isOpen={!!editingRequest}
        onClose={handleCloseEditDialog}
        onSave={handleSaveRequest}
        request={editingRequest}
        categories={categories}
        isSubmitting={isSubmitting}
      />
      
      <Footer />
    </div>
  );
};

export default RequestedReagentsPage;
